// Components
import { Skeleton, Box, Stack, Text } from "@mantine/core";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "@benchmark/ui/components/atomic";
import { AppointmentForm } from "@/components/domain/appointment/Appointment.form";
import { ClinicalImpressionForm } from "@/components/domain/clinical-impression/ClinicalImpression.form";
import { CommunicationForm } from "@/components/domain/communication/Communication.form";
import { EncounterFormShell } from "@/components/domain/encounter/EncounterFormShell";
import { HealthcareServiceForm } from "@/components/domain/healthcare-service/HealthcareService.form";
import { ObservationForm } from "@/components/domain/observation/Observation.form";
import { PatientForm } from "@/components/domain/patient/Patient.form";
import { PractitionerForm } from "@/components/domain/practitioner/Practitioner.form";
import { TaskForm } from "@/components/domain/task/Task.form";
import { FlagForm } from "@/components/domain/flag/Flag.form";
import { ArchiveForm } from "@/components/domain/task/ArchiveTask.form";
import { SnoozeTaskForm } from "@/components/domain/task/SnoozeTask.form";
import { ReassignTaskForm } from "@/components/domain/task/ReassignTask.form";
import { PhoneNumbersModalWrapper } from "@/components/domain/patient/PhoneNumbersModalWrapper";
import { SMSThreadDrawer } from "@/components/domain/communication/SMSThreadDrawer";
import { PausePatientForm } from "@/components/domain/patient/PausePatientForm";
import { UnpausePatientModal } from "@/components/domain/patient/UnpausePatientModal";
import { GoalForm } from "@/components/domain/goal/GoalForm";
import { GoalProgressForm } from "@/components/domain/goal/GoalProgressForm";
import { CloseGoalModal } from "@/components/domain/goal/CloseGoalModal";
import { EnrollmentFlow } from "@/components/domain/care-plan/EnrollmentFlow";
import { CarePlanOverrideForm } from "@/components/domain/care-plan/CarePlanOverrideForm";
import { ActivityCompletionWizard } from "@/components/domain/task/ActivityCompletionWizard";
import { ActivityProgressWizard } from "@/components/domain/task/ActivityProgressWizard";
import { EngagementStatusForm } from "@/components/domain/patient/EngagementStatusForm";
import { PatientSummaryForm } from "@/components/domain/patient/PatientSummaryForm";
import { ReferralUploadModalContent } from "@/components/domain/patient/ReferralUploadModalContent";
import { SurveyLinkModalContent } from "@/components/domain/patient/SurveyLinkModalContent";
import { ENCOUNTER_DOCUMENTATION_MODAL_PROPS } from "@/constants/encounterDocumentationModal";
import { ActivityLogModal } from "@/components/domain/care-time/ActivityLogModal";
import { CareTimeQuickAddModalWrapper } from "@/components/domain/care-time/CareTimeQuickAddModalWrapper";
import { BillingModal } from "@/components/domain/care-time/BillingModal";

// Hooks
import { useDisclosure } from "@mantine/hooks";
import { useModalUrlState } from "@benchmark/ui/hooks";
import type { ModalSearchParams } from "@benchmark/ui/hooks";

// Types
import type {
  Appointment,
  CarePlan,
  ClinicalImpression,
  Communication,
  Encounter,
  HealthcareService,
  Observation,
  Patient,
  Practitioner,
  PractitionerRole,
  Task,
  Flag,
  Goal,
} from "@medplum/fhirtypes";
import type { ReactNode } from "react";
import type { CommunicationFormRef } from "@/components/domain/communication/Communication.form";
import type { EncounterFormRef } from "@/components/domain/encounter/EncounterFormShell";
import type { PatientFormRef } from "@/components/domain/patient/Patient.form";
import type { TaskFormRef } from "@/components/domain/task/Task.form";
import type { ReferralExtractionPayload } from "@/components/domain/patient/ReferralUploadModalContent";
import type { CareTimeMethod } from "@benchmark/core/constants";

// Utils
import { createContext } from "react";
import { getResourceIdFromReference } from "@benchmark/core/utils";

/**
 * Query-param keys that task action modals attach to the URL (for analytics).
 * The param exists only while a task modal is open: every modal open resets it
 * and every close strips it.
 * @agentic connections
 */
const TASK_MODAL_URL_PARAMS = ["patientId"] as const;

/**
 * The task modal URL params mapped to `undefined`, used to clear any stale
 * values whenever a modal is opened.
 * @agentic connections
 */
const CLEARED_TASK_MODAL_URL_PARAMS: ModalSearchParams = Object.fromEntries(
  TASK_MODAL_URL_PARAMS.map((key) => [key, undefined]),
);

// Styles
import styles from "./ModalProvider.module.css";

/**
 * Loading fallback for modal forms with configurable height.
 * @agentic @benchmark/ui
 */
const ModalFormLoadingFallback = ({
  height = "200px",
}: {
  height?: string;
}) => (
  <Box className={styles.loadingBox} style={{ minHeight: height }}>
    <Stack gap="md">
      <Skeleton height={32} width="60%" />
      <Skeleton height={14} width="40%" />
      <Skeleton height={36} />
      <Skeleton height={14} width="30%" />
      <Skeleton height={36} />
      <Skeleton height={44} width="200px" />
    </Stack>
  </Box>
);

/** Options for patient modal. @agentic connections */
export type PatientModalOptions = {
  patient?: Patient;
  action?: "create" | "edit";
  /** Initial form values for pre-filling (e.g., from referral extraction) */
  initialValues?: Record<string, unknown>;
  /** Controls duplicate-check input debouncing for patient creation form. */
  duplicateCheckDebounce?: boolean;
  onSuccess?: (patientId: string) => void;
  onCancel?: () => void;
};

/** Options for encounter modal. @agentic connections */
export type EncounterModalOptions = {
  patientId?: string;
  encounter?: Encounter;
  action?: "create" | "edit";
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: import("@benchmark/core/types").EncounterActivityContext;
  /** When set, only these encounter types are available for selection. */
  restrictToEncounterTypes?: string[];
};

/** Options for communication modal. @agentic connections */
export type CommunicationModalOptions = {
  patientId?: string;
  communication?: Communication;
  action?: "create" | "edit";
  onSuccess?: (communicationId: string) => void;
  onCancel?: () => void;
};

/** Options for task modal. @agentic connections */
export type TaskModalOptions = {
  patientId?: string;
  task?: Task;
  action?: "create" | "edit";
  onSuccess?: (taskId: string) => void;
  onCancel?: () => void;
};

/** Options for flag modal. @agentic connections */
export type FlagModalOptions = {
  patientId?: string;
  flag?: Flag;
  action?: "create" | "edit";
  onSuccess?: (flagId: string) => void;
  onCancel?: () => void;
};

/** Options for appointment modal. @agentic connections */
export type AppointmentModalOptions = {
  patientId?: string;
  appointment?: Appointment;
  action?: "create" | "edit";
  onSuccess?: (appointmentId: string) => void;
  onCancel?: () => void;
};

/** Options for clinical impression modal. @agentic connections */
export type ClinicalImpressionModalOptions = {
  patientId?: string;
  clinicalImpression?: ClinicalImpression;
  action?: "create" | "edit";
  onSuccess?: (clinicalImpressionId: string) => void;
  onCancel?: () => void;
};

/** Options for healthcare service modal. @agentic connections */
export type HealthcareServiceModalOptions = {
  healthcareService?: HealthcareService;
  action?: "create" | "edit";
  /** Initial form values for pre-filling (e.g., from website extraction) */
  initialValues?: Partial<Record<string, unknown>>;
  /** Raw crawled data for fields that couldn't be auto-matched */
  importMeta?: {
    extractedServices: string[] | null;
    extractedLocations: string[] | null;
    extractedInsurance: string[] | null;
  };
  onSuccess?: (healthcareServiceId: string) => void;
  onCancel?: () => void;
};

/** Options for observation modal. @agentic connections */
export type ObservationModalOptions = {
  patientId?: string;
  observation?: Observation;
  action?: "create" | "edit";
  onSuccess?: (observationId: string) => void;
  onCancel?: () => void;
};

/** Options for archive task modal. @agentic connections */
export type ArchiveTaskModalOptions = {
  taskId?: string | null;
  onArchive?: (task: Task, reason: string) => void;
  initialStep?: number;
  readOnlyReview?: boolean;
  onCancel?: () => void;
};

/** Options for snooze task modal. @agentic connections */
export type SnoozeTaskModalOptions = {
  task?: Task;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for reassign task modal. @agentic connections */
export type ReassignTaskModalOptions = {
  task?: Task;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for practitioner modal. @agentic connections */
export type PractitionerModalOptions = {
  practitioner?: Practitioner;
  action?: "create" | "edit";
  onSuccess?: (practitionerId: string, practitionerRoleId?: string) => void;
  onCancel?: () => void;
};

/** Options for call patient modal. @agentic connections */
export type CallPatientModalOptions = {
  patientId?: string;
  onCancel?: () => void;
};

/** Options for SMS thread modal. @agentic connections */
export type SMSThreadModalOptions = {
  patientId?: string;
  onCancel?: () => void;
  onMessageSent?: (communication: Communication) => void;
};

/** Options for pause patient modal. @agentic connections */
export type PausePatientModalOptions = {
  patient?: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for unpause patient modal. @agentic connections */
export type UnpausePatientModalOptions = {
  patient?: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for goal modal. @agentic connections */
export type GoalModalOptions = {
  patientId?: string;
  goal?: Goal;
  action?: "create" | "edit";
  onSuccess?: (goalId: string) => void;
  onCancel?: () => void;
};

/** Options for goal progress modal. @agentic connections */
export type GoalProgressModalOptions = {
  goal: Goal;
  patientId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for close goal modal. @agentic connections */
export type CloseGoalModalOptions = {
  goal: Goal;
  patient: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for enroll care plan modal. @agentic connections */
export type EnrollCarePlanModalOptions = {
  patientId?: string;
  /**
   * Optional preselected care plan type. When set, the modal opens directly
   * to Step 2 with that type chosen — used by the FAB "Add Triage Plan"
   * quick-action.
   */
  preselectCarePlanType?: import("@benchmark/core/types").CarePlanType;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for engagement status modal. @agentic connections */
export type EngagementStatusModalOptions = {
  patient?: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for patient summary modal. @agentic connections */
export type PatientSummaryModalOptions = {
  patient?: Patient;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for referral upload modal. @agentic connections */
export type ReferralUploadModalOptions = {
  onExtracted?: (data: ReferralExtractionPayload) => void;
  onCancel?: () => void;
};

/** Options for patient survey link modal. @agentic connections */
export type SurveyLinkModalOptions = {
  patientId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/** Options for care plan override modal (complete/cancel). @agentic connections */
export type CarePlanOverrideModalOptions = {
  carePlan?: CarePlan;
  action?: "complete" | "cancel";
  warning?: string;
  onConfirm?: (reason: string) => Promise<void>;
  onCancel?: () => void;
};

/** Options for task progress modal. @agentic connections */
export type ProgressTaskModalOptions = {
  task?: Task;
  onComplete?: () => void;
};

/** Options for the stepped CoCM billing workflow modal. @agentic connections */
export type BillingWorkflowModalOptions = {
  patientId?: string;
  /** Billing month as `YYYY-MM`. */
  billingMonth?: string;
  onCancel?: () => void;
};

/** Options for the care-time activity log modal. @agentic connections */
export type ActivityLogModalOptions = {
  patientId?: string;
  patientDisplay?: string;
  onCancel?: () => void;
};

/** Options for the care-time quick-add modal. @agentic connections */
export type CareTimeQuickAddModalOptions = {
  patientId?: string;
  patientDisplay?: string;
  performerRoleId?: string;
  performerDisplay?: string;
  method?: CareTimeMethod;
  onCancel?: () => void;
};

/** Options for task completion modal. @agentic connections */
export type CompleteTaskModalOptions = {
  task?: Task;
  onComplete?: () => void;
};

/** Union of all modal type identifiers. @agentic connections */
export type ModalType =
  | "patient"
  | "encounter"
  | "communication"
  | "task"
  | "flag"
  | "appointment"
  | "clinical-impression"
  | "healthcare-service"
  | "observation"
  | "practitioner"
  | "archive-task"
  | "snooze-task"
  | "reassign-task"
  | "call-patient"
  | "sms-thread"
  | "pause-patient"
  | "unpause-patient"
  | "goal"
  | "goal-progress"
  | "close-goal"
  | "enroll-care-plan"
  | "engagement-status"
  | "patient-summary"
  | "referral-upload"
  | "survey-link"
  | "complete-care-plan"
  | "cancel-care-plan"
  | "complete-task"
  | "progress-task"
  | "activity-log"
  | "quick-add-care-time"
  | "billing-workflow";

/** Modal context interface with openers and management functions. @agentic connections */
export type ModalContextType = {
  // Modal openers
  openPatientModal: (options?: PatientModalOptions) => void;
  openEncounterModal: (options?: EncounterModalOptions) => void;
  openCommunicationModal: (options?: CommunicationModalOptions) => void;
  openTaskModal: (options?: TaskModalOptions) => void;
  openFlagModal: (options?: FlagModalOptions) => void;
  openAppointmentModal: (options?: AppointmentModalOptions) => void;
  openClinicalImpressionModal: (
    options?: ClinicalImpressionModalOptions,
  ) => void;
  openHealthcareServiceModal: (options?: HealthcareServiceModalOptions) => void;
  openObservationModal: (options?: ObservationModalOptions) => void;
  openPractitionerModal: (options?: PractitionerModalOptions) => void;
  openArchiveTaskModal: (options?: ArchiveTaskModalOptions) => void;
  openSnoozeTaskModal: (options?: SnoozeTaskModalOptions) => void;
  openReassignTaskModal: (options?: ReassignTaskModalOptions) => void;
  openCallPatientModal: (options?: CallPatientModalOptions) => void;
  openSMSThreadModal: (options?: SMSThreadModalOptions) => void;
  openPausePatientModal: (options?: PausePatientModalOptions) => void;
  openUnpausePatientModal: (options?: UnpausePatientModalOptions) => void;
  openGoalModal: (options?: GoalModalOptions) => void;
  openGoalProgressModal: (options?: GoalProgressModalOptions) => void;
  openCloseGoalModal: (options?: CloseGoalModalOptions) => void;
  openEnrollCarePlanModal: (options?: EnrollCarePlanModalOptions) => void;
  openEngagementStatusModal: (options?: EngagementStatusModalOptions) => void;
  openPatientSummaryModal: (options?: PatientSummaryModalOptions) => void;
  openReferralUploadModal: (options?: ReferralUploadModalOptions) => void;
  openSurveyLinkModal: (options: SurveyLinkModalOptions) => void;
  openCompleteCarePlanModal: (options?: CarePlanOverrideModalOptions) => void;
  openCancelCarePlanModal: (options?: CarePlanOverrideModalOptions) => void;
  openCompleteTaskModal: (options?: CompleteTaskModalOptions) => void;
  openProgressTaskModal: (options?: ProgressTaskModalOptions) => void;
  openActivityLogModal: (options?: ActivityLogModalOptions) => void;
  openCareTimeQuickAddModal: (options?: CareTimeQuickAddModalOptions) => void;
  openBillingWorkflowModal: (options?: BillingWorkflowModalOptions) => void;

  // Modal management
  closeAllModals: () => void;
  getActiveModals: () => ModalType[];
  isAnyModalOpen: () => boolean;
  isModalOpen: (type: ModalType) => boolean;
};

/** React context for modal management. @agentic connections */
export const ModalContext = createContext<ModalContextType | null>(null);

/** Modal provider configuration. @agentic connections */
type ModalConfig = {
  maxConcurrentModals?: number;
  allowModalStacking?: boolean;
  closeOnNavigate?: boolean;
};

/** Props for ModalProvider. @agentic connections */
type ModalProviderProps = {
  children: ReactNode;
  config?: ModalConfig;
};

/** Modal action type. @agentic connections */
type ModalAction =
  "create" | "edit" | "view" | "archive" | "snooze" | "reassign";

/** Maps URL hash to modal type and action. @agentic connections */
const HASH_TO_MODAL_CONFIG: Record<
  string,
  { type: ModalType; action: ModalAction }
> = {
  "add-goal-progress": { type: "goal-progress", action: "create" },
  "archive-task": { type: "archive-task", action: "archive" },
  "call-patient": { type: "call-patient", action: "view" },
  "close-goal": { type: "close-goal", action: "edit" },
  "create-appointment": { type: "appointment", action: "create" },
  "create-clinical-impression": {
    type: "clinical-impression",
    action: "create",
  },
  "create-communication": { type: "communication", action: "create" },
  "create-encounter": { type: "encounter", action: "create" },
  "create-flag": { type: "flag", action: "create" },
  "create-goal": { type: "goal", action: "create" },
  "create-healthcare-service": { type: "healthcare-service", action: "create" },
  "create-observation": { type: "observation", action: "create" },
  "create-patient": { type: "patient", action: "create" },
  "create-practitioner": { type: "practitioner", action: "create" },
  "create-task": { type: "task", action: "create" },
  "upload-referral": { type: "referral-upload", action: "create" },
  "create-survey-link": { type: "survey-link", action: "create" },
  "edit-appointment": { type: "appointment", action: "edit" },
  "edit-clinical-impression": { type: "clinical-impression", action: "edit" },
  "edit-communication": { type: "communication", action: "edit" },
  "edit-encounter": { type: "encounter", action: "edit" },
  "edit-flag": { type: "flag", action: "edit" },
  "edit-goal": { type: "goal", action: "edit" },
  "edit-healthcare-service": { type: "healthcare-service", action: "edit" },
  "edit-observation": { type: "observation", action: "edit" },
  "edit-patient": { type: "patient", action: "edit" },
  "edit-practitioner": { type: "practitioner", action: "edit" },
  "edit-task": { type: "task", action: "edit" },
  "edit-engagement-status": { type: "engagement-status", action: "edit" },
  "edit-patient-summary": { type: "patient-summary", action: "edit" },
  "enroll-care-plan": { type: "enroll-care-plan", action: "view" },
  "complete-care-plan": { type: "complete-care-plan", action: "edit" },
  "cancel-care-plan": { type: "cancel-care-plan", action: "edit" },
  "complete-task": { type: "complete-task", action: "edit" },
  "progress-task": { type: "progress-task", action: "edit" },
  "pause-patient": { type: "pause-patient", action: "view" },
  "sms-thread": { type: "sms-thread", action: "view" },
  "reassign-task": { type: "reassign-task", action: "edit" },
  "snooze-task": { type: "snooze-task", action: "snooze" },
  "unpause-patient": { type: "unpause-patient", action: "view" },
  "activity-log": { type: "activity-log", action: "view" },
  "quick-add-care-time": { type: "quick-add-care-time", action: "create" },
  "billing-workflow": { type: "billing-workflow", action: "edit" },
};

/** Maps modal type to URL hash for create/edit. @agentic connections */
const MODAL_CONFIG_TO_HASH: Record<
  ModalType,
  { create: string; edit: string }
> = {
  patient: { create: "create-patient", edit: "edit-patient" },
  encounter: { create: "create-encounter", edit: "edit-encounter" },
  communication: { create: "create-communication", edit: "edit-communication" },
  task: { create: "create-task", edit: "edit-task" },
  flag: { create: "create-flag", edit: "edit-flag" },
  appointment: { create: "create-appointment", edit: "edit-appointment" },
  "clinical-impression": {
    create: "create-clinical-impression",
    edit: "edit-clinical-impression",
  },
  "healthcare-service": {
    create: "create-healthcare-service",
    edit: "edit-healthcare-service",
  },
  observation: { create: "create-observation", edit: "edit-observation" },
  practitioner: { create: "create-practitioner", edit: "edit-practitioner" },
  "archive-task": { create: "archive-task", edit: "archive-task" },
  "snooze-task": { create: "snooze-task", edit: "snooze-task" },
  "reassign-task": { create: "reassign-task", edit: "reassign-task" },
  "call-patient": { create: "call-patient", edit: "call-patient" },
  "sms-thread": { create: "sms-thread", edit: "sms-thread" },
  "pause-patient": { create: "pause-patient", edit: "pause-patient" },
  "unpause-patient": { create: "unpause-patient", edit: "unpause-patient" },
  goal: { create: "create-goal", edit: "edit-goal" },
  "goal-progress": { create: "add-goal-progress", edit: "add-goal-progress" },
  "close-goal": { create: "close-goal", edit: "close-goal" },
  "enroll-care-plan": { create: "enroll-care-plan", edit: "enroll-care-plan" },
  "engagement-status": {
    create: "edit-engagement-status",
    edit: "edit-engagement-status",
  },
  "patient-summary": {
    create: "edit-patient-summary",
    edit: "edit-patient-summary",
  },
  "referral-upload": {
    create: "upload-referral",
    edit: "upload-referral",
  },
  "survey-link": {
    create: "create-survey-link",
    edit: "create-survey-link",
  },
  "complete-care-plan": {
    create: "complete-care-plan",
    edit: "complete-care-plan",
  },
  "cancel-care-plan": {
    create: "cancel-care-plan",
    edit: "cancel-care-plan",
  },
  "complete-task": {
    create: "complete-task",
    edit: "complete-task",
  },
  "progress-task": {
    create: "progress-task",
    edit: "progress-task",
  },
  "activity-log": {
    create: "activity-log",
    edit: "activity-log",
  },
  "quick-add-care-time": {
    create: "quick-add-care-time",
    edit: "quick-add-care-time",
  },
  "billing-workflow": {
    create: "billing-workflow",
    edit: "billing-workflow",
  },
};

/**
 * Modal provider that manages all app modals with URL hash sync and form protection.
 * @agentic connections
 */
export const ModalProvider = ({
  children,
  config: _config = {
    maxConcurrentModals: 1,
    allowModalStacking: false,
    closeOnNavigate: true,
  },
}: ModalProviderProps) => {
  // URL state management
  const {
    openModal: openModalRaw,
    closeModal: closeModalRaw,
    currentHash,
    currentSearch,
  } = useModalUrlState();
  // `patientId` that openers append to the URL (see `openModal`). On a
  // full reload / bookmarked link the in-memory modal options are gone,
  // so this lets read-only, patient-scoped modals (the activity log)
  // rehydrate enough context from the URL to re-render.
  const urlPatientId =
    new URLSearchParams(currentSearch).get("patientId") ?? undefined;

  // Wrap open/close so the task-modal analytics params (e.g. patientId) live
  // only while a task modal is open: every open clears stale values (and task
  // openers re-set them), and every close strips them — regardless of which
  // modal's close handler runs.
  const openModal = useCallback(
    (modalType: string, searchParams?: ModalSearchParams) => {
      openModalRaw(modalType, {
        ...CLEARED_TASK_MODAL_URL_PARAMS,
        ...searchParams,
      });
    },
    [openModalRaw],
  );

  const closeModal = useCallback(
    (paramsToRemove?: string[]) => {
      closeModalRaw([...TASK_MODAL_URL_PARAMS, ...(paramsToRemove ?? [])]);
    },
    [closeModalRaw],
  );

  // Modal states using useDisclosure
  const [patientModal, patientModalHandlers] = useDisclosure(false);
  const [encounterModal, encounterModalHandlers] = useDisclosure(false);
  const [communicationModal, communicationModalHandlers] = useDisclosure(false);
  const [taskModal, taskModalHandlers] = useDisclosure(false);
  const [flagModal, flagModalHandlers] = useDisclosure(false);
  const [appointmentModal, appointmentModalHandlers] = useDisclosure(false);
  const [clinicalImpressionModal, clinicalImpressionModalHandlers] =
    useDisclosure(false);
  const [healthcareServiceModal, healthcareServiceModalHandlers] =
    useDisclosure(false);
  const [observationModal, observationModalHandlers] = useDisclosure(false);
  const [practitionerModal, practitionerModalHandlers] = useDisclosure(false);

  const [archiveTaskModal, archiveTaskModalHandlers] = useDisclosure(false);
  const [snoozeTaskModal, snoozeTaskModalHandlers] = useDisclosure(false);
  const [reassignTaskModal, reassignTaskModalHandlers] = useDisclosure(false);
  const [callPatientModal, callPatientModalHandlers] = useDisclosure(false);
  const [smsThreadModal, smsThreadModalHandlers] = useDisclosure(false);
  const [pausePatientModal, pausePatientModalHandlers] = useDisclosure(false);
  const [unpausePatientModal, unpausePatientModalHandlers] =
    useDisclosure(false);
  const [goalModal, goalModalHandlers] = useDisclosure(false);
  const [goalProgressModal, goalProgressModalHandlers] = useDisclosure(false);
  const [_closeGoalModal, closeGoalModalHandlers] = useDisclosure(false);
  const [enrollCarePlanModal, enrollCarePlanModalHandlers] =
    useDisclosure(false);
  const [engagementStatusModal, engagementStatusModalHandlers] =
    useDisclosure(false);
  const [patientSummaryModal, patientSummaryModalHandlers] =
    useDisclosure(false);
  const [referralUploadModal, referralUploadModalHandlers] =
    useDisclosure(false);
  const [surveyLinkModal, surveyLinkModalHandlers] = useDisclosure(false);
  const [completeCarePlanModal, completeCarePlanModalHandlers] =
    useDisclosure(false);
  const [cancelCarePlanModal, cancelCarePlanModalHandlers] =
    useDisclosure(false);

  // Modal options
  const [patientOptions, setPatientOptions] = useState<PatientModalOptions>({});
  const [encounterOptions, setEncounterOptions] =
    useState<EncounterModalOptions>({});
  const [communicationOptions, setCommunicationOptions] =
    useState<CommunicationModalOptions>({});
  const [taskOptions, setTaskOptions] = useState<TaskModalOptions>({});
  const [flagOptions, setFlagOptions] = useState<FlagModalOptions>({});
  const [appointmentOptions, setAppointmentOptions] =
    useState<AppointmentModalOptions>({});
  const [clinicalImpressionOptions, setClinicalImpressionOptions] =
    useState<ClinicalImpressionModalOptions>({});
  const [healthcareServiceOptions, setHealthcareServiceOptions] =
    useState<HealthcareServiceModalOptions>({});
  const [observationOptions, setObservationOptions] =
    useState<ObservationModalOptions>({});
  const [practitionerOptions, setPractitionerOptions] =
    useState<PractitionerModalOptions>({});

  const [archiveTaskOptions, setArchiveTaskOptions] =
    useState<ArchiveTaskModalOptions>({});
  const [snoozeTaskOptions, setSnoozeTaskOptions] =
    useState<SnoozeTaskModalOptions>({});
  const [reassignTaskOptions, setReassignTaskOptions] =
    useState<ReassignTaskModalOptions>({});
  const [callPatientOptions, setCallPatientOptions] =
    useState<CallPatientModalOptions>({});
  const [smsThreadOptions, setSMSThreadOptions] =
    useState<SMSThreadModalOptions>({});
  const [pausePatientOptions, setPausePatientOptions] =
    useState<PausePatientModalOptions>({});
  const [unpausePatientOptions, setUnpausePatientOptions] =
    useState<UnpausePatientModalOptions>({});
  const [goalOptions, setGoalOptions] = useState<GoalModalOptions>({});
  const [goalProgressOptions, setGoalProgressOptions] =
    useState<GoalProgressModalOptions | null>(null);
  const [closeGoalOptions, setCloseGoalOptions] =
    useState<CloseGoalModalOptions | null>(null);
  const [enrollCarePlanOptions, setEnrollCarePlanOptions] =
    useState<EnrollCarePlanModalOptions>({});
  const [engagementStatusOptions, setEngagementStatusOptions] =
    useState<EngagementStatusModalOptions>({});
  const [patientSummaryOptions, setPatientSummaryOptions] =
    useState<PatientSummaryModalOptions>({});
  const [referralUploadOptions, setReferralUploadOptions] =
    useState<ReferralUploadModalOptions>({});
  const [surveyLinkOptions, setSurveyLinkOptions] =
    useState<SurveyLinkModalOptions>({ patientId: "" });
  const [isReferralUploadBusy, setIsReferralUploadBusy] = useState(false);
  const [isReferralDuplicateWarning, setIsReferralDuplicateWarning] =
    useState(false);
  const [completeCarePlanOptions, setCompleteCarePlanOptions] =
    useState<CarePlanOverrideModalOptions>({});
  const [cancelCarePlanOptions, setCancelCarePlanOptions] =
    useState<CarePlanOverrideModalOptions>({});
  const [completeTaskModal, completeTaskModalHandlers] = useDisclosure(false);
  const [completeTaskOptions, setCompleteTaskOptions] =
    useState<CompleteTaskModalOptions>({});
  const [progressTaskModal, progressTaskModalHandlers] = useDisclosure(false);
  const [progressTaskOptions, setProgressTaskOptions] =
    useState<ProgressTaskModalOptions>({});
  const [activityLogModal, activityLogModalHandlers] = useDisclosure(false);
  const [activityLogOptions, setActivityLogOptions] =
    useState<ActivityLogModalOptions>({});
  const [careTimeQuickAddModal, careTimeQuickAddModalHandlers] =
    useDisclosure(false);
  const [billingWorkflowModal, billingWorkflowModalHandlers] =
    useDisclosure(false);
  const [billingWorkflowOptions, setBillingWorkflowOptions] =
    useState<BillingWorkflowModalOptions>({});
  const [careTimeQuickAddOptions, setCareTimeQuickAddOptions] =
    useState<CareTimeQuickAddModalOptions>({});

  // Refs for form protection
  const communicationFormRef = useRef<CommunicationFormRef>(null);
  const encounterFormRef = useRef<EncounterFormRef>(null);
  const patientFormRef = useRef<PatientFormRef>(null);
  const taskFormRef = useRef<TaskFormRef>(null);

  // Watch URL hash and sync modal state
  useEffect(() => {
    const modalConfig = HASH_TO_MODAL_CONFIG[currentHash];

    // Close all modals first (skip patient if we're already closing it)
    appointmentModalHandlers.close();
    archiveTaskModalHandlers.close();
    callPatientModalHandlers.close();
    clinicalImpressionModalHandlers.close();
    communicationModalHandlers.close();
    encounterModalHandlers.close();
    flagModalHandlers.close();
    healthcareServiceModalHandlers.close();
    observationModalHandlers.close();
    patientModalHandlers.close();
    pausePatientModalHandlers.close();
    practitionerModalHandlers.close();
    smsThreadModalHandlers.close();
    snoozeTaskModalHandlers.close();
    reassignTaskModalHandlers.close();
    taskModalHandlers.close();
    unpausePatientModalHandlers.close();
    goalModalHandlers.close();
    goalProgressModalHandlers.close();
    closeGoalModalHandlers.close();
    enrollCarePlanModalHandlers.close();
    engagementStatusModalHandlers.close();
    patientSummaryModalHandlers.close();
    referralUploadModalHandlers.close();
    surveyLinkModalHandlers.close();
    completeCarePlanModalHandlers.close();
    cancelCarePlanModalHandlers.close();
    completeTaskModalHandlers.close();
    progressTaskModalHandlers.close();
    activityLogModalHandlers.close();
    careTimeQuickAddModalHandlers.close();
    billingWorkflowModalHandlers.close();

    // Open the modal corresponding to the hash
    if (modalConfig) {
      if (modalConfig.type === "patient") {
        patientModalHandlers.open();
      } else if (modalConfig.type === "encounter") {
        encounterModalHandlers.open();
      } else if (modalConfig.type === "communication") {
        communicationModalHandlers.open();
      } else if (modalConfig.type === "task") {
        taskModalHandlers.open();
      } else if (modalConfig.type === "flag") {
        flagModalHandlers.open();
      } else if (modalConfig.type === "appointment") {
        appointmentModalHandlers.open();
      } else if (modalConfig.type === "clinical-impression") {
        clinicalImpressionModalHandlers.open();
      } else if (modalConfig.type === "healthcare-service") {
        healthcareServiceModalHandlers.open();
      } else if (modalConfig.type === "observation") {
        observationModalHandlers.open();
      } else if (modalConfig.type === "practitioner") {
        practitionerModalHandlers.open();
      } else if (modalConfig.type === "archive-task") {
        archiveTaskModalHandlers.open();
      } else if (modalConfig.type === "snooze-task") {
        snoozeTaskModalHandlers.open();
      } else if (modalConfig.type === "reassign-task") {
        reassignTaskModalHandlers.open();
      } else if (modalConfig.type === "call-patient") {
        callPatientModalHandlers.open();
      } else if (modalConfig.type === "sms-thread") {
        smsThreadModalHandlers.open();
      } else if (modalConfig.type === "pause-patient") {
        pausePatientModalHandlers.open();
      } else if (modalConfig.type === "unpause-patient") {
        unpausePatientModalHandlers.open();
      } else if (modalConfig.type === "goal") {
        goalModalHandlers.open();
      } else if (modalConfig.type === "goal-progress") {
        goalProgressModalHandlers.open();
      } else if (modalConfig.type === "close-goal") {
        closeGoalModalHandlers.open();
      } else if (modalConfig.type === "enroll-care-plan") {
        enrollCarePlanModalHandlers.open();
      } else if (modalConfig.type === "engagement-status") {
        engagementStatusModalHandlers.open();
      } else if (modalConfig.type === "patient-summary") {
        patientSummaryModalHandlers.open();
      } else if (modalConfig.type === "referral-upload") {
        referralUploadModalHandlers.open();
      } else if (modalConfig.type === "survey-link") {
        surveyLinkModalHandlers.open();
      } else if (modalConfig.type === "complete-care-plan") {
        completeCarePlanModalHandlers.open();
      } else if (modalConfig.type === "cancel-care-plan") {
        cancelCarePlanModalHandlers.open();
      } else if (modalConfig.type === "complete-task") {
        completeTaskModalHandlers.open();
      } else if (modalConfig.type === "progress-task") {
        progressTaskModalHandlers.open();
      } else if (modalConfig.type === "activity-log") {
        activityLogModalHandlers.open();
      } else if (modalConfig.type === "quick-add-care-time") {
        careTimeQuickAddModalHandlers.open();
      } else if (modalConfig.type === "billing-workflow") {
        billingWorkflowModalHandlers.open();
      }
    }
  }, [
    pausePatientModalHandlers,
    appointmentModalHandlers,
    archiveTaskModalHandlers,
    callPatientModalHandlers,
    clinicalImpressionModalHandlers,
    communicationModalHandlers,
    currentHash,
    encounterModalHandlers,
    flagModalHandlers,
    healthcareServiceModalHandlers,
    observationModalHandlers,
    patientModalHandlers,
    practitionerModalHandlers,
    smsThreadModalHandlers,
    snoozeTaskModalHandlers,
    reassignTaskModalHandlers,
    taskModalHandlers,
    unpausePatientModalHandlers,
    goalModalHandlers,
    goalProgressModalHandlers,
    closeGoalModalHandlers,
    enrollCarePlanModalHandlers,
    engagementStatusModalHandlers,
    patientSummaryModalHandlers,
    referralUploadModalHandlers,
    surveyLinkModalHandlers,
    completeCarePlanModalHandlers,
    cancelCarePlanModalHandlers,
    completeTaskModalHandlers,
    progressTaskModalHandlers,
    activityLogModalHandlers,
    careTimeQuickAddModalHandlers,
    billingWorkflowModalHandlers,
  ]);

  // Modal openers
  const openPatientModal = useCallback(
    (options: PatientModalOptions = {}) => {
      setPatientOptions(options);
      // Determine action: if patient exists, it's edit; otherwise create
      const action = options.action || (options.patient ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.patient[action]);
    },
    [openModal],
  );

  const openEncounterModal = useCallback(
    (options: EncounterModalOptions = {}) => {
      setEncounterOptions(options);
      // Determine action: if encounter exists, it's edit; otherwise create
      const action = options.action || (options.encounter ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.encounter[action]);
    },
    [openModal],
  );

  const openCommunicationModal = useCallback(
    (options: CommunicationModalOptions = {}) => {
      setCommunicationOptions(options);
      // Determine action: if communication exists, it's edit; otherwise create
      const action =
        options.action || (options.communication ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.communication[action]);
    },
    [openModal],
  );

  const openTaskModal = useCallback(
    (options: TaskModalOptions = {}) => {
      setTaskOptions(options);
      // Determine action: if task exists, it's edit; otherwise create
      const action = options.action || (options.task ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.task[action]);
    },
    [openModal],
  );

  const openFlagModal = useCallback(
    (options: FlagModalOptions = {}) => {
      setFlagOptions(options);
      const action = options.action || (options.flag ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.flag[action]);
    },
    [openModal],
  );

  const openAppointmentModal = useCallback(
    (options: AppointmentModalOptions = {}) => {
      setAppointmentOptions(options);
      // Determine action: if appointment exists, it's edit; otherwise create
      const action =
        options.action || (options.appointment ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.appointment[action]);
    },
    [openModal],
  );

  const openClinicalImpressionModal = useCallback(
    (options: ClinicalImpressionModalOptions = {}) => {
      setClinicalImpressionOptions(options);
      // Determine action: if clinical impression exists, it's edit; otherwise create
      const action =
        options.action || (options.clinicalImpression ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH["clinical-impression"][action]);
    },
    [openModal],
  );

  const openHealthcareServiceModal = useCallback(
    (options: HealthcareServiceModalOptions = {}) => {
      setHealthcareServiceOptions(options);
      const action =
        options.action || (options.healthcareService ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH["healthcare-service"][action]);
    },
    [openModal],
  );

  const openObservationModal = useCallback(
    (options: ObservationModalOptions = {}) => {
      setObservationOptions(options);
      const action =
        options.action || (options.observation ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH["observation"][action]);
    },
    [openModal],
  );

  const openPractitionerModal = useCallback(
    (options: PractitionerModalOptions = {}) => {
      setPractitionerOptions(options);
      const action =
        options.action || (options.practitioner ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH["practitioner"][action]);
    },
    [openModal],
  );

  const openArchiveTaskModal = useCallback(
    (options: ArchiveTaskModalOptions = {}) => {
      setArchiveTaskOptions(options);
      openModal("archive-task");
    },
    [openModal],
  );

  const openSnoozeTaskModal = useCallback(
    (options: SnoozeTaskModalOptions = {}) => {
      setSnoozeTaskOptions(options);
      openModal("snooze-task");
    },
    [openModal],
  );

  const openReassignTaskModal = useCallback(
    (options: ReassignTaskModalOptions = {}) => {
      setReassignTaskOptions(options);
      openModal("reassign-task");
    },
    [openModal],
  );

  const openCallPatientModal = useCallback(
    (options: CallPatientModalOptions = {}) => {
      setCallPatientOptions(options);
      openModal("call-patient");
    },
    [openModal],
  );

  const openSMSThreadModal = useCallback(
    (options: SMSThreadModalOptions = {}) => {
      setSMSThreadOptions(options);
      openModal("sms-thread");
    },
    [openModal],
  );

  const openPausePatientModal = useCallback(
    (options: PausePatientModalOptions = {}) => {
      setPausePatientOptions(options);
      openModal("pause-patient");
    },
    [openModal],
  );

  const openUnpausePatientModal = useCallback(
    (options: UnpausePatientModalOptions = {}) => {
      setUnpausePatientOptions(options);
      openModal("unpause-patient");
    },
    [openModal],
  );

  const openGoalModal = useCallback(
    (options: GoalModalOptions = {}) => {
      setGoalOptions(options);
      const action = options.action || (options.goal ? "edit" : "create");
      openModal(MODAL_CONFIG_TO_HASH.goal[action]);
    },
    [openModal],
  );

  const openGoalProgressModal = useCallback(
    (options?: GoalProgressModalOptions) => {
      if (!options) return;
      setGoalProgressOptions(options);
      openModal(MODAL_CONFIG_TO_HASH["goal-progress"].create);
    },
    [openModal],
  );

  const openCloseGoalModal = useCallback(
    (options?: CloseGoalModalOptions) => {
      if (!options) return;
      setCloseGoalOptions(options);
      openModal(MODAL_CONFIG_TO_HASH["close-goal"].edit);
    },
    [openModal],
  );

  const openEnrollCarePlanModal = useCallback(
    (options: EnrollCarePlanModalOptions = {}) => {
      setEnrollCarePlanOptions(options);
      openModal("enroll-care-plan");
    },
    [openModal],
  );

  const openEngagementStatusModal = useCallback(
    (options: EngagementStatusModalOptions = {}) => {
      setEngagementStatusOptions(options);
      openModal("edit-engagement-status");
    },
    [openModal],
  );

  const openPatientSummaryModal = useCallback(
    (options: PatientSummaryModalOptions = {}) => {
      setPatientSummaryOptions(options);
      openModal("edit-patient-summary");
    },
    [openModal],
  );

  const openReferralUploadModal = useCallback(
    (options: ReferralUploadModalOptions = {}) => {
      setReferralUploadOptions(options);
      openModal(MODAL_CONFIG_TO_HASH["referral-upload"].create);
    },
    [openModal],
  );

  const openSurveyLinkModal = useCallback(
    (options: SurveyLinkModalOptions) => {
      setSurveyLinkOptions(options);
      openModal(MODAL_CONFIG_TO_HASH["survey-link"].create);
    },
    [openModal],
  );

  const openCompleteCarePlanModal = useCallback(
    (options: CarePlanOverrideModalOptions = {}) => {
      setCompleteCarePlanOptions(options);
      openModal("complete-care-plan");
    },
    [openModal],
  );

  const openCancelCarePlanModal = useCallback(
    (options: CarePlanOverrideModalOptions = {}) => {
      setCancelCarePlanOptions(options);
      openModal("cancel-care-plan");
    },
    [openModal],
  );

  const openCompleteTaskModal = useCallback(
    (options: CompleteTaskModalOptions = {}) => {
      setCompleteTaskOptions(options);
      const patientId = getResourceIdFromReference(
        options.task?.for?.reference,
        "Patient",
      );
      openModal("complete-task", { patientId });
    },
    [openModal],
  );

  const openProgressTaskModal = useCallback(
    (options: ProgressTaskModalOptions = {}) => {
      setProgressTaskOptions(options);
      const patientId = getResourceIdFromReference(
        options.task?.for?.reference,
        "Patient",
      );
      openModal("progress-task", { patientId });
    },
    [openModal],
  );

  const openBillingWorkflowModal = useCallback(
    (options: BillingWorkflowModalOptions = {}) => {
      setBillingWorkflowOptions(options);
      openModal("billing-workflow", { patientId: options.patientId });
    },
    [openModal],
  );

  const openActivityLogModal = useCallback(
    (options: ActivityLogModalOptions = {}) => {
      setActivityLogOptions(options);
      openModal("activity-log", { patientId: options.patientId });
    },
    [openModal],
  );

  const openCareTimeQuickAddModal = useCallback(
    (options: CareTimeQuickAddModalOptions = {}) => {
      setCareTimeQuickAddOptions(options);
      openModal("quick-add-care-time", { patientId: options.patientId });
    },
    [openModal],
  );

  // Modal management functions
  const closeAllModals = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const getActiveModals = useCallback((): ModalType[] => {
    const modalConfig = HASH_TO_MODAL_CONFIG[currentHash];
    return modalConfig ? [modalConfig.type] : [];
  }, [currentHash]);

  const isAnyModalOpen = useCallback(() => {
    return Boolean(HASH_TO_MODAL_CONFIG[currentHash]);
  }, [currentHash]);

  const isModalOpen = useCallback(
    (type: ModalType) => {
      const modalConfig = HASH_TO_MODAL_CONFIG[currentHash];
      return modalConfig ? modalConfig.type === type : false;
    },
    [currentHash],
  );

  // Modal close handlers - now integrate with form protection
  const handlePatientModalClose = useCallback(async () => {
    // Check form protection before closing
    if (patientFormRef.current) {
      try {
        const canClose = await patientFormRef.current.checkProtection();
        if (!canClose) {
          return; // Don't close if form protection prevents it
        }
      } catch (error) {
        console.error("Error in protection check:", error);
        // If protection check fails, allow close to prevent being stuck
      }
    }

    patientOptions.onCancel?.();
    closeModal();
  }, [patientOptions, closeModal]);

  const handleEncounterModalClose = useCallback(async () => {
    // Check form protection before closing
    if (encounterFormRef.current) {
      try {
        const canClose = await encounterFormRef.current.checkProtection();
        if (!canClose) {
          return; // Don't close if form protection prevents it
        }
      } catch (error) {
        console.error("Error in protection check:", error);
        // If protection check fails, allow close to prevent being stuck
      }
    }

    encounterOptions.onCancel?.();
    closeModal();
  }, [encounterOptions, closeModal]);

  const handleCommunicationModalClose = useCallback(async () => {
    // Check form protection before closing
    if (communicationFormRef.current) {
      try {
        const canClose = await communicationFormRef.current.checkProtection();
        if (!canClose) {
          return; // Don't close if form protection prevents it
        }
      } catch (error) {
        console.error("Error in protection check:", error);
        // If protection check fails, allow close to prevent being stuck
      }
    }

    communicationOptions.onCancel?.();
    closeModal();
  }, [communicationOptions, closeModal]);

  const handleTaskModalClose = useCallback(async () => {
    if (taskFormRef.current) {
      try {
        const canClose = await taskFormRef.current.checkProtection();
        if (!canClose) {
          return;
        }
      } catch (error) {
        console.error("Error in protection check:", error);
      }
    }

    taskOptions.onCancel?.();
    closeModal();
  }, [taskOptions, closeModal]);

  const handleAppointmentModalClose = useCallback(() => {
    appointmentOptions.onCancel?.();
    closeModal();
  }, [appointmentOptions, closeModal]);

  const handleClinicalImpressionModalClose = useCallback(() => {
    clinicalImpressionOptions.onCancel?.();
    closeModal();
  }, [clinicalImpressionOptions, closeModal]);

  const handleHealthcareServiceModalClose = useCallback(() => {
    healthcareServiceOptions.onCancel?.();
    closeModal();
  }, [healthcareServiceOptions, closeModal]);

  const handleObservationModalClose = useCallback(() => {
    observationOptions.onCancel?.();
    closeModal();
  }, [observationOptions, closeModal]);

  const handlePractitionerModalClose = useCallback(() => {
    practitionerOptions.onCancel?.();
    closeModal();
  }, [practitionerOptions, closeModal]);

  const handleArchiveTaskModalClose = useCallback(() => {
    archiveTaskOptions.onCancel?.();
    closeModal();
  }, [archiveTaskOptions, closeModal]);

  const handleSnoozeTaskModalClose = useCallback(() => {
    snoozeTaskOptions.onCancel?.();
    closeModal();
  }, [snoozeTaskOptions, closeModal]);

  const handleReassignTaskModalClose = useCallback(() => {
    reassignTaskOptions.onCancel?.();
    closeModal();
  }, [reassignTaskOptions, closeModal]);

  const handleCallPatientModalClose = useCallback(() => {
    callPatientOptions.onCancel?.();
    closeModal();
  }, [callPatientOptions, closeModal]);

  const handleSMSThreadModalClose = useCallback(() => {
    smsThreadOptions.onCancel?.();
    closeModal();
  }, [smsThreadOptions, closeModal]);

  const handlePausePatientModalClose = useCallback(() => {
    pausePatientOptions.onCancel?.();
    closeModal();
  }, [pausePatientOptions, closeModal]);

  const handleUnpausePatientModalClose = useCallback(() => {
    unpausePatientOptions.onCancel?.();
    closeModal();
  }, [unpausePatientOptions, closeModal]);

  const handleGoalModalClose = useCallback(() => {
    goalOptions.onCancel?.();
    closeModal();
  }, [goalOptions, closeModal]);

  const handleGoalProgressModalClose = useCallback(() => {
    goalProgressOptions?.onCancel?.();
    setGoalProgressOptions(null);
    closeModal();
  }, [goalProgressOptions, closeModal]);

  const handleCloseGoalModalClose = useCallback(() => {
    closeGoalOptions?.onCancel?.();
    setCloseGoalOptions(null);
    closeModal();
  }, [closeGoalOptions, closeModal]);

  const handleEnrollCarePlanModalClose = useCallback(() => {
    enrollCarePlanOptions.onCancel?.();
    closeModal();
  }, [enrollCarePlanOptions, closeModal]);

  const handleEngagementStatusModalClose = useCallback(() => {
    engagementStatusOptions.onCancel?.();
    closeModal();
  }, [engagementStatusOptions, closeModal]);

  const handlePatientSummaryModalClose = useCallback(() => {
    patientSummaryOptions.onCancel?.();
    closeModal();
  }, [patientSummaryOptions, closeModal]);

  const handleReferralUploadModalClose = useCallback(() => {
    setIsReferralUploadBusy(false);
    setIsReferralDuplicateWarning(false);
    referralUploadOptions.onCancel?.();
    closeModal();
  }, [closeModal, referralUploadOptions]);

  const handleSurveyLinkModalClose = useCallback(() => {
    surveyLinkOptions.onCancel?.();
    closeModal();
  }, [closeModal, surveyLinkOptions]);

  const handleReferralUploadExtracted = useCallback(
    (data: ReferralExtractionPayload) => {
      setIsReferralUploadBusy(false);
      closeModal();
      referralUploadOptions.onExtracted?.(data);
    },
    [closeModal, referralUploadOptions],
  );

  // Store callbacks in refs to prevent form re-renders
  const completeCarePlanOnCancelRef = useRef<(() => void) | undefined>(
    undefined,
  );
  const completeCarePlanOnConfirmRef = useRef<
    ((reason: string) => Promise<void>) | undefined
  >(undefined);
  const cancelCarePlanOnCancelRef = useRef<(() => void) | undefined>(undefined);
  const cancelCarePlanOnConfirmRef = useRef<
    ((reason: string) => Promise<void>) | undefined
  >(undefined);

  useEffect(() => {
    completeCarePlanOnCancelRef.current = completeCarePlanOptions.onCancel;
    completeCarePlanOnConfirmRef.current = completeCarePlanOptions.onConfirm;
  }, [completeCarePlanOptions.onCancel, completeCarePlanOptions.onConfirm]);

  useEffect(() => {
    cancelCarePlanOnCancelRef.current = cancelCarePlanOptions.onCancel;
    cancelCarePlanOnConfirmRef.current = cancelCarePlanOptions.onConfirm;
  }, [cancelCarePlanOptions.onCancel, cancelCarePlanOptions.onConfirm]);

  const handleCompleteCarePlanModalClose = useCallback(() => {
    completeCarePlanOnCancelRef.current?.();
    closeModal();
  }, [closeModal]);

  const handleCompleteCarePlanFormCancel = useCallback(() => {
    // Form cancel handler - just close the modal
    // The ref callback will be called by handleCompleteCarePlanModalClose
    closeModal();
  }, [closeModal]);

  // onConfirm performs the FHIR write only. The form calls onSuccess
  // (handleCompleteCarePlanFormSuccess) AFTER markAsClean() so the
  // navigation-blocker doesn't intercept the route-aware closeModal().
  const handleCompleteCarePlanConfirm = useCallback(async (reason: string) => {
    await completeCarePlanOnConfirmRef.current?.(reason);
  }, []);

  const handleCompleteCarePlanFormSuccess = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleCancelCarePlanModalClose = useCallback(() => {
    cancelCarePlanOnCancelRef.current?.();
    closeModal();
  }, [closeModal]);

  const handleCancelCarePlanFormCancel = useCallback(() => {
    // Form cancel handler - just close the modal
    // The ref callback will be called by handleCancelCarePlanModalClose
    closeModal();
  }, [closeModal]);

  const handleCancelCarePlanFormSuccess = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // onConfirm performs the FHIR write only; the form's onSuccess hook
  // (handleCancelCarePlanFormSuccess) closes the modal AFTER
  // markAsClean() so the form-protection blocker doesn't fire.
  const handleCancelCarePlanConfirm = useCallback(async (reason: string) => {
    await cancelCarePlanOnConfirmRef.current?.(reason);
  }, []);

  const handleCompleteTaskModalClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleTaskCompleted = useCallback(() => {
    completeTaskOptions.onComplete?.();
    closeModal();
  }, [completeTaskOptions, closeModal]);

  const handleProgressTaskModalClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleProgressTaskCompleted = useCallback(() => {
    progressTaskOptions.onComplete?.();
    closeModal();
  }, [progressTaskOptions, closeModal]);

  const handleActivityLogModalClose = useCallback(() => {
    activityLogOptions.onCancel?.();
    closeModal();
  }, [activityLogOptions, closeModal]);

  const handleBillingWorkflowModalClose = useCallback(() => {
    billingWorkflowOptions.onCancel?.();
    closeModal();
  }, [billingWorkflowOptions, closeModal]);

  const handleCareTimeQuickAddModalClose = useCallback(() => {
    careTimeQuickAddOptions.onCancel?.();
    closeModal();
  }, [careTimeQuickAddOptions, closeModal]);

  // Success handlers
  const handlePatientSuccess = useCallback(
    (patientId: string) => {
      patientOptions.onSuccess?.(patientId);
      closeModal();
    },
    [patientOptions, closeModal],
  );

  const handleEncounterSuccess = useCallback(
    (encounterId: string) => {
      encounterOptions.onSuccess?.(encounterId);
      closeModal();
    },
    [encounterOptions, closeModal],
  );

  const handleCommunicationSuccess = useCallback(
    (communicationId: string) => {
      communicationOptions.onSuccess?.(communicationId);
      closeModal();
    },
    [communicationOptions, closeModal],
  );

  const handleTaskSuccess = useCallback(
    (taskId: string) => {
      taskOptions.onSuccess?.(taskId);
      closeModal();
    },
    [taskOptions, closeModal],
  );

  const handleAppointmentSuccess = useCallback(
    (appointmentId: string) => {
      appointmentOptions.onSuccess?.(appointmentId);
      closeModal();
    },
    [appointmentOptions, closeModal],
  );

  const handleClinicalImpressionSuccess = useCallback(
    (clinicalImpressionId: string) => {
      clinicalImpressionOptions.onSuccess?.(clinicalImpressionId);
      closeModal();
    },
    [clinicalImpressionOptions, closeModal],
  );

  const handleHealthcareServiceSuccess = useCallback(
    (healthcareServiceId: string) => {
      healthcareServiceOptions.onSuccess?.(healthcareServiceId);
      closeModal();
    },
    [healthcareServiceOptions, closeModal],
  );

  const handleObservationSuccess = useCallback(
    (observationId: string) => {
      observationOptions.onSuccess?.(observationId);
      closeModal();
    },
    [observationOptions, closeModal],
  );

  const handlePractitionerSuccess = useCallback(
    (practitionerId: string, practitionerRoleId?: string) => {
      practitionerOptions.onSuccess?.(practitionerId, practitionerRoleId);
      closeModal();
    },
    [practitionerOptions, closeModal],
  );

  const handleGoalSuccess = useCallback(
    (goalId: string) => {
      goalOptions.onSuccess?.(goalId);
      closeModal();
    },
    [goalOptions, closeModal],
  );

  const handleGoalProgressSuccess = useCallback(() => {
    goalProgressOptions?.onSuccess?.();
    setGoalProgressOptions(null);
    closeModal();
  }, [goalProgressOptions, closeModal]);

  const handleCloseGoalSuccess = useCallback(() => {
    closeGoalOptions?.onSuccess?.();
    setCloseGoalOptions(null);
    closeModal();
  }, [closeGoalOptions, closeModal]);

  // Get current modal action for dynamic titles
  const getCurrentModalAction = useCallback(() => {
    const modalConfig = HASH_TO_MODAL_CONFIG[currentHash];
    return modalConfig ? modalConfig.action : null;
  }, [currentHash]);

  const contextValue: ModalContextType = useMemo(
    () => ({
      closeAllModals,
      getActiveModals,
      isAnyModalOpen,
      isModalOpen,
      openAppointmentModal,
      openClinicalImpressionModal,
      openCommunicationModal,
      openEncounterModal,
      openHealthcareServiceModal,
      openObservationModal,
      openPatientModal,
      openPractitionerModal,
      openTaskModal,
      openFlagModal,
      openArchiveTaskModal,
      openSnoozeTaskModal,
      openReassignTaskModal,
      openCallPatientModal,
      openSMSThreadModal,
      openPausePatientModal,
      openUnpausePatientModal,
      openGoalModal,
      openGoalProgressModal,
      openCloseGoalModal,
      openEnrollCarePlanModal,
      openEngagementStatusModal,
      openPatientSummaryModal,
      openReferralUploadModal,
      openSurveyLinkModal,
      openCompleteCarePlanModal,
      openCancelCarePlanModal,
      openCompleteTaskModal,
      openProgressTaskModal,
      openActivityLogModal,
      openCareTimeQuickAddModal,
      openBillingWorkflowModal,
    }),
    [
      closeAllModals,
      getActiveModals,
      isAnyModalOpen,
      isModalOpen,
      openAppointmentModal,
      openClinicalImpressionModal,
      openCommunicationModal,
      openEncounterModal,
      openHealthcareServiceModal,
      openObservationModal,
      openPatientModal,
      openPractitionerModal,
      openTaskModal,
      openFlagModal,
      openArchiveTaskModal,
      openSnoozeTaskModal,
      openReassignTaskModal,
      openCallPatientModal,
      openSMSThreadModal,
      openPausePatientModal,
      openUnpausePatientModal,
      openGoalModal,
      openGoalProgressModal,
      openCloseGoalModal,
      openEnrollCarePlanModal,
      openEngagementStatusModal,
      openPatientSummaryModal,
      openReferralUploadModal,
      openSurveyLinkModal,
      openCompleteCarePlanModal,
      openCancelCarePlanModal,
      openCompleteTaskModal,
      openProgressTaskModal,
      openActivityLogModal,
      openCareTimeQuickAddModal,
      openBillingWorkflowModal,
    ],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}

      {/* Patient Modal */}
      <Modal
        opened={patientModal}
        onClose={handlePatientModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Create New Member"
            : "Edit Member"
        }
        size="xl"
        closeOnClickOutside={true}
        closeOnEscape={true}
      >
        <Suspense fallback={<ModalFormLoadingFallback height="500px" />}>
          <PatientForm
            ref={patientFormRef}
            patient={patientOptions.patient}
            initialValues={patientOptions.initialValues}
            duplicateCheckDebounce={patientOptions.duplicateCheckDebounce}
            onSuccess={handlePatientSuccess}
            onCancel={handlePatientModalClose}
          />
        </Suspense>
      </Modal>

      {/* Encounter Modal - with form protection */}
      <Modal
        opened={encounterModal}
        onClose={handleEncounterModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Log New Interaction"
            : "Edit Interaction"
        }
        {...ENCOUNTER_DOCUMENTATION_MODAL_PROPS}
      >
        <Suspense fallback={<ModalFormLoadingFallback height="600px" />}>
          <EncounterFormShell
            ref={encounterFormRef}
            patientId={encounterOptions.patientId}
            encounter={encounterOptions.encounter}
            activityContext={encounterOptions.activityContext}
            restrictToEncounterTypes={encounterOptions.restrictToEncounterTypes}
            onSuccess={handleEncounterSuccess}
            onCancel={handleEncounterModalClose}
          />
        </Suspense>
      </Modal>

      {/* Communication Modal */}
      <Modal
        opened={communicationModal}
        onClose={handleCommunicationModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Log Outreach Attempt"
            : "Edit Outreach"
        }
        {...ENCOUNTER_DOCUMENTATION_MODAL_PROPS}
      >
        <Suspense fallback={<ModalFormLoadingFallback height="300px" />}>
          <CommunicationForm
            ref={communicationFormRef}
            patientId={communicationOptions.patientId}
            communication={communicationOptions.communication}
            onSuccess={handleCommunicationSuccess}
            onCancel={handleCommunicationModalClose}
          />
        </Suspense>
      </Modal>

      {/* Task Modal */}
      <Modal
        opened={taskModal}
        onClose={handleTaskModalClose}
        title={
          getCurrentModalAction() === "create" ? "Add New Task" : "Edit Task"
        }
        size="lg"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="400px" />}>
          <TaskForm
            ref={taskFormRef}
            patientId={taskOptions.patientId}
            task={taskOptions.task}
            onSuccess={handleTaskSuccess}
            onCancel={handleTaskModalClose}
          />
        </Suspense>
      </Modal>

      {/* Flag Modal */}
      <Modal
        opened={flagModal}
        onClose={() => {
          flagOptions.onCancel?.();
          closeModal();
        }}
        title={getCurrentModalAction() === "create" ? "Add Flag" : "Edit Flag"}
        size="md"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="220px" />}>
          <FlagForm
            patientId={flagOptions.patientId}
            flag={flagOptions.flag}
            onSuccess={(flagId: string) => {
              flagOptions.onSuccess?.(flagId);
              closeModal();
            }}
            onCancel={() => {
              flagOptions.onCancel?.();
              closeModal();
            }}
          />
        </Suspense>
      </Modal>

      {/* Appointment Modal */}
      <Modal
        opened={appointmentModal}
        onClose={handleAppointmentModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Schedule New Appointment"
            : "Edit Appointment"
        }
        size="xl"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="450px" />}>
          <AppointmentForm
            patientId={appointmentOptions.patientId}
            appointment={appointmentOptions.appointment}
            onSuccess={handleAppointmentSuccess}
            onCancel={handleAppointmentModalClose}
          />
        </Suspense>
      </Modal>

      {/* Clinical Impression Modal */}
      <Modal
        opened={clinicalImpressionModal}
        onClose={handleClinicalImpressionModalClose}
        title={
          getCurrentModalAction() === "create" ? "Add New Note" : "Edit Note"
        }
        size="lg"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="300px" />}>
          <ClinicalImpressionForm
            patientId={clinicalImpressionOptions.patientId}
            clinicalImpression={clinicalImpressionOptions.clinicalImpression}
            onSuccess={handleClinicalImpressionSuccess}
            onCancel={handleClinicalImpressionModalClose}
          />
        </Suspense>
      </Modal>

      {/* Healthcare Service Modal */}
      <Modal
        opened={healthcareServiceModal}
        onClose={handleHealthcareServiceModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Add New Service"
            : "Edit Service"
        }
        size="xl"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="400px" />}>
          <HealthcareServiceForm
            healthcareService={healthcareServiceOptions.healthcareService}
            initialValues={healthcareServiceOptions.initialValues}
            importMeta={healthcareServiceOptions.importMeta}
            onSuccess={handleHealthcareServiceSuccess}
            onCancel={handleHealthcareServiceModalClose}
          />
        </Suspense>
      </Modal>

      {/* Observation Modal */}
      <Modal
        opened={observationModal}
        onClose={handleObservationModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Log Assessment Score"
            : "Edit Assessment Score"
        }
        size="lg"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="350px" />}>
          <ObservationForm
            patientId={observationOptions.patientId}
            observation={observationOptions.observation}
            onSuccess={handleObservationSuccess}
            onCancel={handleObservationModalClose}
          />
        </Suspense>
      </Modal>

      {/* Practitioner Modal */}
      <Modal
        opened={practitionerModal}
        onClose={handlePractitionerModalClose}
        title={
          getCurrentModalAction() === "create"
            ? "Add New Provider"
            : "Edit Provider"
        }
        size="xl"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="400px" />}>
          <PractitionerForm
            onPractitionerCreated={(
              practitioner: Practitioner,
              practitionerRole: PractitionerRole,
            ) => {
              handlePractitionerSuccess(practitioner.id!, practitionerRole.id);
            }}
            onComplete={handlePractitionerModalClose}
          />
        </Suspense>
      </Modal>

      {/* Archive Task Modal */}
      <Modal
        opened={archiveTaskModal}
        onClose={handleArchiveTaskModalClose}
        title="Archive Task"
        size="xl"
      >
        <ArchiveForm
          opened={archiveTaskModal}
          taskId={archiveTaskOptions.taskId}
          onArchive={archiveTaskOptions.onArchive}
          initialStep={archiveTaskOptions.initialStep}
          readOnlyReview={archiveTaskOptions.readOnlyReview}
          onCancel={handleArchiveTaskModalClose}
        />
      </Modal>

      {/* Snooze Task Modal */}
      <Modal
        opened={snoozeTaskModal}
        onClose={handleSnoozeTaskModalClose}
        title="Snooze Task"
        size="md"
      >
        {snoozeTaskOptions.task ? (
          <SnoozeTaskForm
            task={snoozeTaskOptions.task}
            onSuccess={() => {
              snoozeTaskOptions.onSuccess?.();
              handleSnoozeTaskModalClose();
            }}
            onCancel={handleSnoozeTaskModalClose}
          />
        ) : (
          <Text>No task selected.</Text>
        )}
      </Modal>

      {/* Reassign Task Modal */}
      <Modal
        opened={reassignTaskModal}
        onClose={handleReassignTaskModalClose}
        title="Reassign Task"
        size="md"
      >
        {reassignTaskOptions.task ? (
          <ReassignTaskForm
            task={reassignTaskOptions.task}
            onSuccess={() => {
              reassignTaskOptions.onSuccess?.();
              handleReassignTaskModalClose();
            }}
            onCancel={handleReassignTaskModalClose}
          />
        ) : (
          <Text>No task selected.</Text>
        )}
      </Modal>

      {/* Call Patient Modal */}
      {callPatientModal && callPatientOptions.patientId && (
        <PhoneNumbersModalWrapper
          opened={callPatientModal}
          onClose={handleCallPatientModalClose}
          patientId={callPatientOptions.patientId}
        />
      )}

      {/* SMS Thread Drawer — mounted unconditionally so its slide animation
          can play (Mantine's Transition only animates on an `opened` update,
          not on mount/unmount). The drawer defers all patient data loading
          until it is first opened; see SMSThreadDrawer. */}
      <SMSThreadDrawer
        opened={smsThreadModal}
        onClose={handleSMSThreadModalClose}
        patientId={smsThreadOptions.patientId}
        onMessageSent={smsThreadOptions.onMessageSent}
      />

      {/* Pause Patient Modal */}
      <Modal
        opened={pausePatientModal}
        onClose={handlePausePatientModalClose}
        title="Pause Patient"
        size="md"
      >
        {pausePatientOptions.patient ? (
          <PausePatientForm
            patient={pausePatientOptions.patient}
            onSuccess={() => {
              pausePatientOptions.onSuccess?.();
              handlePausePatientModalClose();
            }}
            onCancel={handlePausePatientModalClose}
          />
        ) : (
          <Text>No patient selected.</Text>
        )}
      </Modal>

      {/* Unpause Patient Modal */}
      {unpausePatientOptions.patient && (
        <UnpausePatientModal
          opened={unpausePatientModal}
          onClose={handleUnpausePatientModalClose}
          patient={unpausePatientOptions.patient}
          onSuccess={() => {
            unpausePatientOptions.onSuccess?.();
            handleUnpausePatientModalClose();
          }}
        />
      )}

      {/* Goal Modal */}
      <Modal
        opened={goalModal}
        onClose={handleGoalModalClose}
        title={
          getCurrentModalAction() === "create" ? "Create Goal" : "Edit Goal"
        }
        size="lg"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="300px" />}>
          {goalOptions.patientId && (
            <GoalForm
              patientId={goalOptions.patientId}
              goal={goalOptions.goal}
              onSuccess={handleGoalSuccess}
              onCancel={handleGoalModalClose}
            />
          )}
        </Suspense>
      </Modal>

      {/* Goal Progress Modal */}
      <Modal
        opened={goalProgressModal}
        onClose={handleGoalProgressModalClose}
        title="Add Progress Update"
        size="lg"
      >
        <Suspense fallback={<ModalFormLoadingFallback height="300px" />}>
          {goalProgressOptions && goalProgressOptions.patientId && (
            <GoalProgressForm
              goal={goalProgressOptions.goal}
              patientId={goalProgressOptions.patientId}
              onSuccess={handleGoalProgressSuccess}
              onCancel={handleGoalProgressModalClose}
            />
          )}
        </Suspense>
      </Modal>

      {/* Close Goal Modal - component has its own Modal wrapper */}
      {closeGoalOptions && (
        <Suspense fallback={<ModalFormLoadingFallback height="400px" />}>
          <CloseGoalModal
            goal={closeGoalOptions.goal}
            patient={closeGoalOptions.patient}
            onSuccess={handleCloseGoalSuccess}
            onCancel={handleCloseGoalModalClose}
          />
        </Suspense>
      )}

      {/* Enroll Care Plan Modal */}
      {enrollCarePlanOptions.patientId && (
        <Modal
          opened={enrollCarePlanModal}
          onClose={handleEnrollCarePlanModalClose}
          title={
            enrollCarePlanOptions.preselectCarePlanType === "triage"
              ? "Add Triage Plan"
              : "Enroll in Care Plan"
          }
          size="xl"
        >
          <Suspense fallback={<ModalFormLoadingFallback height="300px" />}>
            <EnrollmentFlow
              patientId={enrollCarePlanOptions.patientId}
              preselectCarePlanType={
                enrollCarePlanOptions.preselectCarePlanType
              }
              onSuccess={() => {
                enrollCarePlanOptions.onSuccess?.();
                handleEnrollCarePlanModalClose();
              }}
              onCancel={handleEnrollCarePlanModalClose}
            />
          </Suspense>
        </Modal>
      )}

      {/* Engagement Status Modal */}
      <Modal
        opened={engagementStatusModal}
        onClose={handleEngagementStatusModalClose}
        title="Override Engagement Status"
        size="md"
      >
        {engagementStatusOptions.patient ? (
          <EngagementStatusForm
            patient={engagementStatusOptions.patient}
            onSuccess={() => {
              engagementStatusOptions.onSuccess?.();
              handleEngagementStatusModalClose();
            }}
            onCancel={handleEngagementStatusModalClose}
          />
        ) : (
          <Text>No patient selected.</Text>
        )}
      </Modal>

      {/* Patient Summary Modal */}
      <Modal
        opened={patientSummaryModal}
        onClose={handlePatientSummaryModalClose}
        title="Edit Patient Summary"
        size="md"
      >
        {patientSummaryOptions.patient ? (
          <PatientSummaryForm
            patient={patientSummaryOptions.patient}
            onSuccess={() => {
              patientSummaryOptions.onSuccess?.();
              handlePatientSummaryModalClose();
            }}
            onCancel={handlePatientSummaryModalClose}
          />
        ) : (
          <Text>No patient selected.</Text>
        )}
      </Modal>

      {/* Survey link modal */}
      <Modal
        opened={surveyLinkModal}
        onClose={handleSurveyLinkModalClose}
        title="Create a survey link"
        size="lg"
      >
        {surveyLinkOptions.patientId ? (
          <SurveyLinkModalContent
            patientId={surveyLinkOptions.patientId}
            onSuccess={surveyLinkOptions.onSuccess}
            onCancel={handleSurveyLinkModalClose}
          />
        ) : (
          <Text>No member selected.</Text>
        )}
      </Modal>

      {/* Referral Upload Modal */}
      <Modal
        opened={referralUploadModal}
        onClose={handleReferralUploadModalClose}
        title={
          isReferralDuplicateWarning
            ? "Potential Duplicate Found"
            : "Upload Referral PDF"
        }
        size="md"
        preventClose={isReferralUploadBusy}
        showCloseButton={!isReferralUploadBusy}
      >
        <ReferralUploadModalContent
          onExtracted={handleReferralUploadExtracted}
          onCancel={handleReferralUploadModalClose}
          onBusyChange={setIsReferralUploadBusy}
          onDuplicateWarningChange={setIsReferralDuplicateWarning}
        />
      </Modal>

      {/* Complete Care Plan Modal */}
      <Modal
        opened={completeCarePlanModal}
        onClose={handleCompleteCarePlanModalClose}
        title="Complete Care Plan"
        size="md"
      >
        {completeCarePlanOptions.carePlan ? (
          <CarePlanOverrideForm
            key={completeCarePlanOptions.carePlan.id}
            carePlan={completeCarePlanOptions.carePlan}
            action={completeCarePlanOptions.action || "complete"}
            warning={completeCarePlanOptions.warning}
            onConfirm={handleCompleteCarePlanConfirm}
            onSuccess={handleCompleteCarePlanFormSuccess}
            onCancel={handleCompleteCarePlanFormCancel}
          />
        ) : (
          <Text>No care plan selected.</Text>
        )}
      </Modal>

      {/* Cancel Care Plan Modal */}
      <Modal
        opened={cancelCarePlanModal}
        onClose={handleCancelCarePlanModalClose}
        title="Cancel Care Plan"
        size="md"
      >
        {cancelCarePlanOptions.carePlan ? (
          <CarePlanOverrideForm
            key={cancelCarePlanOptions.carePlan.id}
            carePlan={cancelCarePlanOptions.carePlan}
            action={cancelCarePlanOptions.action || "cancel"}
            warning={cancelCarePlanOptions.warning}
            onConfirm={handleCancelCarePlanConfirm}
            onSuccess={handleCancelCarePlanFormSuccess}
            onCancel={handleCancelCarePlanFormCancel}
          />
        ) : (
          <Text>No care plan selected.</Text>
        )}
      </Modal>

      {/* Complete Task Modal */}
      <ActivityCompletionWizard
        opened={completeTaskModal}
        onClose={handleCompleteTaskModalClose}
        task={completeTaskOptions.task ?? null}
        onComplete={handleTaskCompleted}
      />

      {/* Progress Task Modal */}
      <ActivityProgressWizard
        opened={progressTaskModal}
        onClose={handleProgressTaskModalClose}
        task={progressTaskOptions.task ?? null}
        onComplete={handleProgressTaskCompleted}
      />

      {/* Care-Time Activity Log Modal — read-only, so it rehydrates its
          patientId from the URL after a reload / deep link. */}
      {activityLogModal && (activityLogOptions.patientId ?? urlPatientId) ? (
        <ActivityLogModal
          opened={activityLogModal}
          onClose={handleActivityLogModalClose}
          patientId={(activityLogOptions.patientId ?? urlPatientId) as string}
          patientDisplay={activityLogOptions.patientDisplay ?? ""}
        />
      ) : null}

      {/* CoCM Billing Workflow Modal — rehydrates patientId from the URL on
          reload / deep link, like the read-only care-time modals. */}
      {billingWorkflowModal &&
      (billingWorkflowOptions.patientId ?? urlPatientId) &&
      billingWorkflowOptions.billingMonth ? (
        <BillingModal
          opened={billingWorkflowModal}
          onClose={handleBillingWorkflowModalClose}
          patientId={
            (billingWorkflowOptions.patientId ?? urlPatientId) as string
          }
          billingMonth={billingWorkflowOptions.billingMonth}
        />
      ) : null}

      {/* Care-Time Quick-Add Modal */}
      {careTimeQuickAddModal &&
      careTimeQuickAddOptions.patientId &&
      careTimeQuickAddOptions.performerRoleId ? (
        <CareTimeQuickAddModalWrapper
          opened={careTimeQuickAddModal}
          onClose={handleCareTimeQuickAddModalClose}
          method={careTimeQuickAddOptions.method ?? "patient-profile-quick-add"}
          patientId={careTimeQuickAddOptions.patientId}
          patientDisplay={careTimeQuickAddOptions.patientDisplay ?? ""}
          performerRoleId={careTimeQuickAddOptions.performerRoleId}
          performerDisplay={
            careTimeQuickAddOptions.performerDisplay ?? "Practitioner"
          }
        />
      ) : null}

      {/* Global confirmation modal is now handled by individual form components */}
    </ModalContext.Provider>
  );
};
